<script>
/**
 * Automatically detect tooltip (label with suffix _tooltip) and add them if exist
 */
import { mergeData } from 'vue-functional-data-merge';

// Is the requested button a link?
const isLink = props => {
  // If tag prop is set to `a`, we use a b-link to get proper disabled handling
  return Boolean(props.href || props.to || (props.tag && String(props.tag).toLowerCase() === 'a'));
};

// Is the button "really" a button?
const isButton = props => {
  if (isLink(props)) {
    return false;
  } else if (props.tag && String(props.tag).toLowerCase() !== 'button') {
    return false;
  }
  return true;
};

// Compute the attributes for a button
const computeAttrs = (props, data) => {
  const button = isButton(props);
  const link = isLink(props);

  const attrs = {
    // Type only used for "real" buttons
    type: button && !link ? props.type : null,
    // Disabled only set on "real" buttons
    disabled: button ? props.disabled : null,
  };

  if (props.selected != null) {
    attrs['aria-selected'] = String(props.selected);
  }

  if (props.ariaHidden) {
    // From the fourth rule of aria: "Do not use role="presentation" or aria-hidden="true" on a focusable element."
    attrs['aria-hidden'] = 'true';
    attrs.tabindex = -1;
  }

  return attrs;
};

export default {
  name: 'Button',

  functional: true,

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: false,
      default: null,
    },
    tag: {
      type: String,
      default: 'button',
    },
    label: {
      type: [Number, String],
      default: null,
    },
    to: {
      type: Object,
      default: null,
    },
    ariaHidden: {
      type: Boolean,
      default: false,
    },
    /** will add class "active" */
    active: {
      type: Boolean,
      required: false,
    },
    /** will add aria-selected="true/false" and append a sr-only span " (currently selected)" */
    selected: {
      type: Boolean,
      required: false,
      // eslint-disable-next-line vue/no-boolean-default
      default: null,
    },
  },

  render(h, { props, data, children, parent }) {
    const on = {
      click(e) {
        /* istanbul ignore if: blink/button disabled should handle this */
        if (props.disabled && e instanceof Event) {
          e.stopPropagation();
          e.preventDefault();
        }
      },
    };

    const componentData = {
      staticClass: 'btn',
      class: [{ disabled: props.disabled, active: props.active }],
      attrs: computeAttrs(props, data),
      on,
    };

    if (children === undefined) children = [];

    if (props.label) {
      children.push(h('span', { domProps: { innerHTML: parent.$to(props.label) } }));
    }

    if (props.selected) {
      children.push(h('span', { staticClass: 'sr-only' }, ' (' + parent.$t('common.currently_active') + ')'));
    }

    if (!data.attrs?.title && props.label) data.attrs.title = parent.$tt(props.label);

    if (props.to) {
      return h('RouterLink', mergeData(mergeData(data, componentData), { props }), children);
    }
    return h(props.tag, mergeData(data, componentData), children);
  },
};
</script>
